import { Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

/**
 * Dashboard info banner (ticket 27756).
 *
 * A plain light-blue info box shown at the top of the dashboard. It builds up
 * to two lines of content from the user's last survey and last weight entry:
 * a "thank you" line when something was done recently, or a reminder line when
 * it is overdue. Dismissible for the current session (no persistence).
 */
@Component({
  selector: 'app-dashboard-info-banner',
  templateUrl: './dashboard-info-banner.component.html',
  styleUrls: ['./dashboard-info-banner.component.scss'],
})
export class DashboardInfoBannerComponent implements OnChanges {
  /** ISO date of the latest completed survey submission. */
  @Input() public lastSurveyDate: string | null = null;

  /** ISO date of the latest weight entry. */
  @Input() public lastWeightDate: string | null = null;

  public lines: string[] = [];
  private dismissed = false;

  private static readonly SURVEY_RECENT_MAX_DAYS = 7;
  private static readonly SURVEY_REMINDER_MIN_DAYS = 30;
  private static readonly WEIGHT_RECENT_MAX_DAYS = 1;
  private static readonly WEIGHT_REMINDER_MIN_DAYS = 7;

  public constructor(private readonly translate: TranslateService) {}

  public ngOnChanges(): void {
    this.lines = [this.buildSurveyLine(), this.buildWeightLine()].filter(
      (line): line is string => Boolean(line)
    );
  }

  public get visible(): boolean {
    return !this.dismissed && this.lines.length > 0;
  }

  public dismiss(): void {
    this.dismissed = true;
  }

  private buildSurveyLine(): string | null {
    const days = this.daysSince(this.lastSurveyDate);
    if (days === null) {
      return null;
    }
    if (days <= DashboardInfoBannerComponent.SURVEY_RECENT_MAX_DAYS) {
      return this.translate.instant('dashboard.info_banner.survey_recent', {
        when: this.whenLabel(days),
      });
    }
    if (days > DashboardInfoBannerComponent.SURVEY_REMINDER_MIN_DAYS) {
      return this.translate.instant('dashboard.info_banner.survey_reminder', { days });
    }
    return null;
  }

  private buildWeightLine(): string | null {
    const days = this.daysSince(this.lastWeightDate);
    if (days === null) {
      return null;
    }
    if (days <= DashboardInfoBannerComponent.WEIGHT_RECENT_MAX_DAYS) {
      return this.translate.instant('dashboard.info_banner.weight_recent', {
        when: this.whenLabel(days),
      });
    }
    if (days > DashboardInfoBannerComponent.WEIGHT_REMINDER_MIN_DAYS) {
      return this.translate.instant('dashboard.info_banner.weight_reminder', { days });
    }
    return null;
  }

  /** Whole days between the given date (start of day) and today. */
  private daysSince(date: string | null): number | null {
    if (!date) {
      return null;
    }
    const days = moment().startOf('day').diff(moment(date).startOf('day'), 'days');
    return days >= 0 ? days : 0;
  }

  private whenLabel(days: number): string {
    if (days <= 0) {
      return this.translate.instant('dashboard.info_banner.today');
    }
    if (days === 1) {
      return this.translate.instant('dashboard.info_banner.yesterday');
    }
    return this.translate.instant('dashboard.info_banner.days_ago', { days });
  }
}
